<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <DirectoryImport
        v-if="isItemBeingUpdated"
        :displayName="directoryName"
        :gSuiteId="item.id"
        :parentType="parentType"
      />
      <DirectorySync
        v-if="isItemBeingCreated || isItemBeingUpdated"
        :deactivateAction="deactivateAction"
        :displayName="item.name"
        :isItemBeingCreated="isItemBeingCreated"
        :isItemBeingUpdated="isItemBeingUpdated"
        :parentType="parentType"
        :reactivateAction="reactivateAction"
        @externalWindowClosed="refreshAndRedirect"
      >
        <DistributionGroupManagement
          :checkboxLabel="checkboxLabel"
          :parentType="parentType"
          :tooltipKbLink="tooltipKbLink"
          :tooltipText="tooltipText"
          tooltipLinkText="Google Workspace Integration Guide"
        />
      </DirectorySync>
      <div
        v-if="isItemBeingUpdated"
        :class="$style.attributeExportWrapper"
      >
        <UserAttributesExport
          :activated="true"
          :directoryName="directoryName"
          :exportAttributesKbUrl="getExportAttributesKbUrl"
          kbLinkText="To learn more, read Syncing User Attributes with Google Workspace"
        />
      </div>
    </template>
  </DetailsAsideLayout>
</template>
<script>
import * as Constants from 'client/util/Constants';
import { get, noop } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util/index';
import AsidePanelChildComponentMethodsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import DirectoryImport from 'client/directories/components/DirectoryImport.vue';
import DirectorySync from 'client/directories/components/DirectorySync.vue';
import DistributionGroupManagement from 'client/directories/components/DistributionGroupManagement.vue';
import GSuiteApi from 'client/api/v2/GSuiteApi';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import UserAttributesExport from 'client/directories/components/UserAttributesExport.vue';
import directoryRouteMixin from 'client/components/mixins/directoryRouteMixin';

const {
  DirectoryNameConsts,
  DirectoryTypeConsts,
  ItemTypeConsts,
  LocalStorageConsts,
  StoreModelMap,
  StoreModuleMap,
} = Constants;

const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];
const gSuiteModel = StoreModelMap[ItemTypeConsts.gSuite];

export default {
  name: 'GSuiteDetails',

  components: {
    DetailsAsideLayout,
    DirectoryImport,
    DirectorySync,
    DistributionGroupManagement,
    UserAttributesExport,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    directoryRouteMixin,
  ],

  props: {
    handleOauthRedirect: {
      type: Function,
      required: true,
    },
  },

  data: () => ({
    announcement: {
      header: 'Import all new attributes!',
      localStorageKey: LocalStorageConsts.importAdditionalGoogleAttributeDismissed,
      text: 'Import or export any user attribute that is not JumpCloud owned. Attributes set to “Import” will be brought into JumpCloud with new users the next time you do a Google Workspace import.',
    },
    directoryName: DirectoryNameConsts[DirectoryTypeConsts.gSuite],
  }),

  computed: {
    ...mapGetters(gSuiteModel, [
      'getExportAttributesKbUrl',
      'item',
    ]),

    checkboxLabel() {
      return `Enable management of groups and memberships in ${this.directoryName}`;
    },

    tooltipKbLink() {
      return KbArticleUrls.gSuiteImportManageGWGroups;
    },

    tooltipText() {
      return `Each group associated with this ${this.directoryName} directory that you want to manage as a distribution group needs a populated email address attribute. When you save this update, you’re asked to reactivate your directory sync. It can take up to 72 hours for ${this.directoryName} to complete the membership sync.`;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    ...mapActions(directoryCollection, [
      'refreshCollection',
    ]),

    /**
     * @todo The DirectoryCollection doesn't delete items correctly because deleting an AD Domain
     * makes a different request than deleting a Google instance. The former calls DELETE
     * /api/v2/activedirectories/:id and the latter calls DELETE /tokens/googleapps. This will
     * be resolved by moving this logic to the DirectoryCollection at a later date.
     */
    deactivateAction() {
      GSuiteApi
        .delete(this.item.id)
        .then(() => this.refreshAndRedirect())
        .catch((error) => {
          let message = `<strong>Unable to delete ${this.directoryName}!</strong>`;

          if (error && error.message) {
            message = `${message}<br>${error.message}`;
          }

          notify(Constants.default.error, message);
        });
    },

    reactivateAction() {
      GSuiteApi
        .reactivate(this.item.id)
        .then((reactivateUrl) => {
          const oauthUrl = get(reactivateUrl, 'data.location');
          this.handleOauthRedirect(oauthUrl);
        })
        .catch((error) => {
          let message = `<strong>Unable to reactivate ${this.directoryName}!</strong>`;

          if (error && error.message) {
            message = `${message}<br>${error.message}`;
          }

          notify(Constants.default.error, message);
        });
    },

    refreshAndRedirect() {
      this.$router.push(this.directoryTypeRoute(DirectoryTypeConsts.gSuite)).catch(noop);
      this.refreshCollection();
    },
  },
};
</script>
<style module>
@import '../../css/sections.css';

.attributeExportWrapper {
  position: relative;
}

.importAttributesAnnouncement {
  left: 1rem;
  position: absolute;
  top: 1rem;
  transform: translateY(-100%);
}
</style>
